// ============================================================
// web/src/lib/api.js — le client API.
// ------------------------------------------------------------
// Le client d'AGC est marqué NE PAS REPRENDRE dans REFERENCE-AGC.md
// pour deux raisons, corrigées ici :
//
//  - aucun timeout : un serveur qui ne répond pas laissait un écran
//    en chargement indéfini, sans aucun moyen de savoir pourquoi ;
//  - `location.reload()` sur un 401 : le devis en cours de saisie
//    était perdu à chaque expiration de session.
//
// Ici : un timeout par défaut, un timeout long sur les routes IA et
// PDF, un parsing défensif (un HTML d'erreur renvoyé par un proxy ne
// doit pas produire un « Unexpected token < »), et un ÉVÉNEMENT sur
// 401 — l'application bascule sur l'écran de connexion en gardant son
// état en mémoire.
// ============================================================

import { CSRF_HEADER, FILENAME_HEADER } from '@shared/http.js';

const DEFAULT_TIMEOUT_MS = 15000;
/** Génération de PDF, dépôt de document : ces routes prennent du temps. */
export const LONG_TIMEOUT_MS = 120000;

/**
 * Lecture d'une page par l'agent 1.
 *
 * Mesuré sur trois passes du corpus : de 13 s à 108 s par page, médiane
 * 26 s. Avec 120 s, une page dense faisait expirer le NAVIGATEUR pendant
 * que le serveur travaillait encore — et l'utilisateur voyait une
 * erreur sur une lecture qui allait aboutir. Le budget serveur est de
 * 540 s (3 tentatives de 180 s) ; celui-ci le suit.
 */
export const READ_TIMEOUT_MS = 420000;

/**
 * Base d'URL de l'API.
 *
 * En développement, `VITE_API_URL` est absente et le proxy Vite renvoie
 * `/api` sur l'API locale. En production, `web/.env.production` fournit
 * l'URL absolue de `api.mertensmec.be`, parce que le front est servi par
 * un AUTRE domaine — `app.mertensmec.be` — en `file_server`.
 *
 * Ce repli sur `/api` a coûté une panne complète de la connexion le
 * 31 juillet 2026 : sans `.env.production`, tout le front postait sur
 * `app.`, où Caddy sert des fichiers statiques avec
 * `try_files {path} /index.html`. Un POST y devient une demande de
 * fichier et repart en **405**, jamais en 401. Et ça ne se voit pas en
 * développement, où le proxy fait exactement ce qu'on attend.
 *
 * D'où la règle : **aucun appel réseau ne contourne ce module.** Un
 * `fetch('/api/...')` écrit à la main quelque part dans `features/`
 * marche en développement et casse en ligne. Un test de build le
 * vérifie désormais sur le bundle produit.
 */
const BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Exportée pour les tests et pour le diagnostic. */
export const API_BASE = BASE;

/** Émis sur 401. L'application écoute et bascule sur la connexion. */
export const AUTH_EXPIRED_EVENT = 'mertens:auth-expired';

export class ApiError extends Error {
  constructor({ status, code, message, details, incidentId }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details ?? null;
    this.incidentId = incidentId ?? null;
  }

  /** Erreur par champ, à afficher à côté du champ fautif. */
  fieldError(field) {
    return this.details?.[field] ?? null;
  }
}

/**
 * Le jeton anti-CSRF, tenu EN MÉMOIRE.
 *
 * Il vient du corps de `/auth/login` et de `/auth/me`. Le cookie qui le
 * porte existe toujours et part à chaque requête — c'est lui que le
 * serveur compare à l'en-tête — mais le front ne peut pas le LIRE :
 * posé par `api.mertensmec.be` sans attribut `Domain`, il est invisible
 * depuis `app.mertensmec.be`.
 *
 * C'est ce qui a tué toutes les écritures en production le 3 août 2026,
 * pendant que la connexion, elle, fonctionnait — le navigateur envoie
 * le cookie, il ne le donne simplement pas à lire au script d'un autre
 * domaine. Le repli sur `document.cookie` reste en place pour un
 * déploiement où front et API partagent la même origine.
 */
let csrfInMemory = null;

export function setCsrfToken(token) {
  if (token) csrfInMemory = token;
}

export function clearCsrfToken() {
  csrfInMemory = null;
}

function csrfToken() {
  if (csrfInMemory) return csrfInMemory;
  const match = document.cookie.match(/(?:^|;\s*)mertens_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Quand `fetch` échoue SANS réponse, dire laquelle des quatre causes.
 *
 * Un `TypeError` de `fetch` ne distingue pas : serveur éteint, DNS
 * mort, requête bloquée par le navigateur (préflight CORS refusé),
 * service redémarré en cours d'appel. Un seul message pour quatre
 * causes est un message qui répond sans discriminer — le même défaut
 * que les 22 pages sorties en `medium`.
 *
 * La sonde tranche : `/health` est une requête SIMPLE au sens CORS —
 * aucun en-tête personnalisé, donc aucun préflight. Si elle passe alors
 * que l'appel a échoué, le serveur est debout et c'est le NAVIGATEUR
 * qui a bloqué : en-tête non autorisé par le CORS, presque toujours.
 * Si elle échoue aussi, le serveur est réellement injoignable.
 *
 * C'est un aller-retour de plus, uniquement sur le chemin d'erreur.
 */
async function diagnoseNetworkFailure() {
  try {
    const probe = await fetch(`${BASE}/health`, {
      method: 'GET',
      // Volontairement SANS `credentials` et SANS en-tête : le moindre
      // ajout rendrait la sonde non simple, elle déclencherait un
      // préflight à son tour et ne trancherait plus rien.
      signal: AbortSignal.timeout(5000),
    });
    if (probe.ok || probe.status < 500) {
      return new ApiError({
        status: 0,
        code: 'blocked_by_browser',
        message:
          'Le serveur répond, mais le navigateur a refusé d’envoyer cette requête. ' +
          'C’est presque toujours un en-tête non autorisé côté API (CORS). ' +
          'Signalez-le : ce n’est pas une panne de réseau.',
      });
    }
  } catch {
    // La sonde échoue aussi : le serveur est bien hors d'atteinte.
  }
  return new ApiError({
    status: 0,
    code: 'network',
    message: 'Serveur injoignable. Vérifiez votre connexion, ou le service est en redémarrage.',
  });
}

/** Un refus de taille vient de Caddy et n'a pas de corps JSON. */
function tooLargeError(maxHint) {
  return new ApiError({
    status: 413,
    code: 'too_large',
    message: `Fichier trop volumineux${maxHint ? ` : maximum ${maxHint}` : ''}. Le serveur l’a refusé avant de le lire.`,
  });
}

/**
 * Lecture défensive du corps. Un proxy en panne renvoie du HTML avec un
 * en-tête JSON, un 502 renvoie une page Caddy : dans les deux cas on ne
 * doit pas produire une erreur de syntaxe incompréhensible.
 */
async function readBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { _raw: text.slice(0, 500) };
  }
}

async function request(path, { method = 'GET', body, timeout = DEFAULT_TIMEOUT_MS, signal } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new Error('timeout')), timeout);
  // Un abandon demandé par l'appelant (démontage de composant) doit
  // aussi couper la requête.
  if (signal) signal.addEventListener('abort', () => controller.abort(signal.reason));

  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (!['GET', 'HEAD'].includes(method)) {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }

  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      // Indispensable : le jeton de session est un cookie HttpOnly.
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    if (controller.signal.aborted && String(controller.signal.reason?.message) === 'timeout') {
      throw new ApiError({
        status: 0,
        code: 'timeout',
        message: 'Le serveur met trop de temps à répondre.',
      });
    }
    if (controller.signal.aborted) throw err; // abandon volontaire
    throw await diagnoseNetworkFailure();
  } finally {
    clearTimeout(timer);
  }

  const payload = await readBody(response);

  if (response.status === 401) {
    // On ne recharge PAS la page : l'application garde son état et
    // bascule sur l'écran de connexion.
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    throw new ApiError({
      status: 401,
      code: payload?.error?.code ?? 'unauthorized',
      message: payload?.error?.message ?? 'Session expirée.',
    });
  }

  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: payload?.error?.code ?? 'http_error',
      message: payload?.error?.message ?? `Erreur ${response.status}.`,
      details: payload?.error?.details,
      incidentId: payload?.error?.incident_id,
    });
  }

  return payload;
}

/**
 * Récupère un document binaire (PDF) et le rend en `Blob`.
 *
 * Passe par la MÊME base d'URL, le même cookie de session et le même
 * jeton anti-CSRF que le reste. Cette fonction existe précisément pour
 * qu'aucun écran n'ait de raison d'appeler `fetch` en direct : c'est ce
 * que faisait `DocumentsCard`, avec un chemin relatif, donc une
 * génération de PDF cassée en production pour la même raison que la
 * connexion.
 *
 * On ne met jamais de jeton dans l'URL : ça fuiterait dans les journaux
 * de Caddy et dans l'historique du navigateur.
 */
export async function fetchBlob(
  path,
  { method = 'GET', body, timeout = LONG_TIMEOUT_MS, avecReponse = false } = {},
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new Error('timeout')), timeout);

  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (!['GET', 'HEAD'].includes(method)) {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }

  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted && String(controller.signal.reason?.message) === 'timeout') {
      throw new ApiError({
        status: 0,
        code: 'timeout',
        message: 'La génération du document met trop de temps.',
      });
    }
    if (controller.signal.aborted) throw err;
    throw await diagnoseNetworkFailure();
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    throw new ApiError({ status: 401, code: 'unauthorized', message: 'Session expirée.' });
  }

  if (!response.ok) {
    // Un refus arrive en JSON : il dit quelle pièce est incomplète et
    // ce qui lui manque. C'est ce message qu'il faut montrer.
    const payload = await readBody(response);
    throw new ApiError({
      status: response.status,
      code: payload?.error?.code ?? 'http_error',
      message: payload?.error?.message ?? `Erreur ${response.status}.`,
      details: payload?.error?.details,
      incidentId: payload?.error?.incident_id,
    });
  }

  const blob = await response.blob();
  return avecReponse ? { blob, response } : blob;
}

/**
 * Le nom de fichier annoncé par le serveur, lu dans `Content-Disposition`.
 *
 * C'est la SOURCE UNIQUE du nom : le serveur l'a déjà construit
 * (`2026-08-063.pdf`), et le recomposer ici en ferait une seconde, qui
 * dériverait au premier changement de numérotation.
 *
 * Rend `null` quand l'en-tête est absent — sur une origine qui ne
 * l'expose pas, ou sur une réponse qui n'en porte pas. L'appelant décide
 * alors, plutôt que d'hériter d'un nom inventé ici.
 */
export function nomDeFichier(response) {
  const brut = response.headers.get('content-disposition');
  if (!brut) return null;
  // `filename*=UTF-8''…` d'abord : c'est la forme qui porte les accents.
  const etoile = /filename\*=UTF-8''([^;]+)/i.exec(brut);
  if (etoile) { try { return decodeURIComponent(etoile[1]); } catch { /* forme cassée */ } }
  const simple = /filename="?([^";]+)"?/i.exec(brut);
  return simple ? simple[1] : null;
}

/**
 * Un document, AVEC son nom.
 *
 * `fetchBlob` rend des octets, et des octets n'ont pas de nom : c'est ce
 * qui faisait enregistrer les devis sous un identifiant de blob. Cette
 * fonction rend les deux, et laisse le nom venir du serveur.
 */
export async function fetchDocument(path, options = {}) {
  const { blob, response } = await fetchBlob(path, { ...options, avecReponse: true });
  return { blob, filename: nomDeFichier(response) };
}

/**
 * Envoie des octets bruts (un PDF déposé par le deviseur).
 *
 * Passe par le MÊME module que tout le reste — base d'URL, cookie de
 * session, jeton anti-CSRF — parce qu'un `fetch` écrit à la main dans
 * un écran marche en développement et casse en ligne. C'est la panne du
 * 31 juillet 2026, et `bundle.test.js` la garde fermée.
 *
 * Pas de multipart : un seul fichier par requête, le nom d'origine dans
 * un en-tête, et le serveur qui vérifie les octets magiques. Une
 * dépendance de moins des deux côtés.
 */
async function postBinary(
  path,
  bytes,
  {
    filename = 'fichier.pdf', timeout = LONG_TIMEOUT_MS, maxHint = '20 Mo',
    // Un aiguillage, pas une preuve : le serveur juge sur les octets.
    type = 'application/pdf',
  } = {},
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new Error('timeout')), timeout);
  const headers = {
    Accept: 'application/json',
    'Content-Type': type,
    // Le nom peut porter des accents : un en-tête HTTP ne les accepte
    // pas tels quels. Le serveur le décode et ne s'en sert que pour
    // l'affichage.
    [FILENAME_HEADER]: encodeURIComponent(filename),
  };
  const token = csrfToken();
  if (token) headers[CSRF_HEADER] = token;

  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: bytes,
      signal: controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted && String(controller.signal.reason?.message) === 'timeout') {
      throw new ApiError({ status: 0, code: 'timeout', message: 'Le dépôt a pris trop de temps.' });
    }
    if (controller.signal.aborted) throw err;
    throw await diagnoseNetworkFailure();
  } finally {
    clearTimeout(timer);
  }

  // Un 413 vient de CADDY, pas de l'API : il n'a pas de corps JSON, donc
  // `payload?.error?.message` serait vide et l'utilisateur lirait
  // « Erreur 413 ». Le message existait déjà dans ce fichier ; il
  // n'était branché nulle part — c'est ESLint qui l'a signalé le jour où
  // il a enfin tourné.
  if (response.status === 413) throw tooLargeError(maxHint);

  const payload = await readBody(response);
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    throw new ApiError({ status: 401, code: 'unauthorized', message: 'Session expirée.' });
  }
  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: payload?.error?.code ?? 'http_error',
      message: payload?.error?.message ?? `Erreur ${response.status}.`,
      details: payload?.error?.details,
      incidentId: payload?.error?.incident_id,
    });
  }
  return payload;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  postBinary,
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};

export default api;
